import { Dialog, InputV2 } from '@cimplico/components';
import { useState } from 'react';
import { useUpdateWorkpaperProcedures } from '../../queries/use-update-workpaper-procedures';
import { useFirmNotApplicableDescriptions } from '../../queries/use-firm-not-applicable-descriptions';
import { WorkpaperProcedureStatusEnum } from '../../types/workpaper-procedure';
import { toastNotification } from '../../components/toast/Toast';
import plural from '../../utilities/plural';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../intercom-events';

type WorkpaperProcedureNotApplicableDialogProps = {
    workpaperId: string;
    procedureIds: string[];
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
};

function WorkpaperProcedureNotApplicableConfirmDialog({
    isOpen,
    procedureIds,
    workpaperId,
    onClose,
    onUpdate,
}: WorkpaperProcedureNotApplicableDialogProps): JSX.Element {
    const [reason, setReason] = useState('');
    const { trackEvent } = useIntercom();
    const notApplicableDescriptionsQuery = useFirmNotApplicableDescriptions();
    const updateProceduresQuery = useUpdateWorkpaperProcedures(workpaperId);

    function updateProcedure(): void {
        trackEvent(intercomEvents.procedure.notApplicable.event);
        updateProceduresQuery.mutate(
            procedureIds.map((id) => ({
                id,
                fields: {
                    status: WorkpaperProcedureStatusEnum.NOT_APPLICABLE,
                    notApplicableReason: reason.trim() === '' ? undefined : reason.trim(),
                },
            })),
            {
                onSuccess: () => {
                    setReason('');
                    onUpdate();
                    if (procedureIds.length > 1) {
                        toastNotification('success', `${procedureIds.length} procedures marked as not applicable!`);
                    }
                },
            }
        );
    }

    function handleClose(): void {
        setReason('');
        onClose();
    }

    return (
        <Dialog
            description={`Do you want to mark ${procedureIds.length} ${plural(
                procedureIds.length,
                'procedure',
                'procedures'
            )} as Not Applicable? You may give a reason as to why.`}
            onClose={handleClose}
            onPrimaryActionClick={updateProcedure}
            onSecondaryActionClick={handleClose}
            isOpen={isOpen}
            primaryAction="Confirm"
            secondaryAction="Cancel"
            title="Not Applicable"
            type="error"
            isLoading={updateProceduresQuery.isLoading}
        >
            <div className="pr-6 pb-5">
                <InputV2
                    type="text"
                    title="Reason"
                    placeholder="Enter your reason"
                    onChange={setReason}
                    value={reason}
                    options={notApplicableDescriptionsQuery.data?.notApplicableDescriptions?.map((d) => d.description) || []}
                    autoFocus
                    isOptional
                />
            </div>
        </Dialog>
    );
}

export default WorkpaperProcedureNotApplicableConfirmDialog;
